/**
 * Turn an unknown thrown value into something worth showing a user.
 *
 * The reason this exists: `err instanceof Error` is false for everything
 * supabase-js rejects with. A PostgrestError is a plain object
 * (`{ message, code, details, hint }`), so the very common
 *
 *     err instanceof Error ? err.message : 'Unknown error'
 *
 * throws away the only useful part and prints "Unknown error" instead. A
 * statement timeout, an RLS denial and a network blip all looked identical.
 */

/** The shape supabase-js rejects with. Structural, so it also covers the
 *  Edge Function / auth error objects that carry the same fields. */
export interface PostgrestErrorLike {
  message: string;
  code?: string;
  details?: string | null;
  hint?: string | null;
}

function isPostgrestErrorLike(value: unknown): value is PostgrestErrorLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof (value as { message: unknown }).message === 'string'
  );
}

/**
 * Postgres SQLSTATEs (and PostgREST codes) worth translating out of
 * database-speak. Anything not listed falls through to the raw message.
 */
const FRIENDLY_BY_CODE: Record<string, string> = {
  // canceling statement due to statement timeout
  '57014':
    'That took too long and the database cancelled it. Narrow the date range or filters and try again.',
  // admin_shutdown / connection terminated
  '57P01': 'The database connection dropped. Please try again.',
  // insufficient_privilege — an RLS policy said no
  '42501': "You don't have permission to view this data.",
  // PostgREST: JWT expired or missing
  PGRST301: 'Your session expired. Sign in again to continue.',
};

/**
 * Best available human-readable message for a caught value.
 *
 * @param error    the value from a `catch` block or a supabase `{ error }`
 * @param fallback used only when nothing usable can be extracted
 */
export function getErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (typeof error === 'string' && error.trim()) return error;

  if (isPostgrestErrorLike(error)) {
    const code = typeof error.code === 'string' ? error.code : undefined;
    if (code && FRIENDLY_BY_CODE[code]) return FRIENDLY_BY_CODE[code];
    if (error.message.trim()) return error.message;
  }

  if (error instanceof Error && error.message.trim()) return error.message;

  return fallback;
}

/**
 * True when retrying the same request stands a real chance of succeeding —
 * timeouts and dropped connections, not permission or validation failures.
 * Used to decide whether to offer a Retry button.
 */
export function isRetryableError(error: unknown): boolean {
  if (!isPostgrestErrorLike(error)) return error instanceof Error;
  return error.code === '57014' || error.code === '57P01' || error.code === undefined;
}
